import { useRef, useEffect, useState } from 'react';
import { Scene, SceneType, PipPosition, OVERLAY_TEMPLATES, PIP_POSITIONS } from '../types';
import { CaptionSettings, EDGE_STYLES } from './SettingsPanel';
import { ComponentPreview } from './ComponentPreview';

// Remotion render dimensions - LOCKED
const RENDER_WIDTH = 1080;
const RENDER_HEIGHT = 1920;

// Layout specs from LAYOUT_REFERENCE.md
const LAYOUTS = {
  SPEAKER_ONLY: {
    avatar: { top: 0, left: 0, width: RENDER_WIDTH, height: RENDER_HEIGHT },
    objectPosition: 'center 40%',
  },
  SPLIT_SCREEN: {
    broll: { top: 0, left: 0, width: RENDER_WIDTH, height: 845 }, // 44%
    avatar: { top: 845, left: 0, width: RENDER_WIDTH, height: 1075 }, // 56%
    avatarObjectPosition: 'center 35%',
  },
  CIRCLE_PIP: {
    broll: { top: 0, left: 0, width: RENDER_WIDTH, height: RENDER_HEIGHT },
    pip: { width: 280, height: 280, margin: 40 },
    avatarObjectPosition: 'center 40%',
  },
  FULL_BROLL: {
    broll: { top: 0, left: 0, width: RENDER_WIDTH, height: RENDER_HEIGHT },
  },
  FULL_BLEED_BROLL: {
    broll: { top: 0, left: 0, width: RENDER_WIDTH, height: RENDER_HEIGHT },
  },
  OVERLAY: {
    // Uses overlay template settings
  },
};

// PIP position calculations (relative to 1080x1920)
function getPipCoords(position: PipPosition = 'bottom-right') {
  const pip = LAYOUTS.CIRCLE_PIP.pip;
  const posConfig = PIP_POSITIONS.find(p => p.id === position);
  
  if (!posConfig) {
    // Default bottom-right
    return { 
      top: RENDER_HEIGHT - pip.height - pip.margin - 280, // Account for caption space
      left: RENDER_WIDTH - pip.width - pip.margin 
    };
  }
  
  const coords = posConfig.coords;
  return {
    top: coords.top ?? (coords.bottom ? RENDER_HEIGHT - pip.height - coords.bottom : RENDER_HEIGHT / 2 - pip.height / 2),
    left: coords.left ?? (coords.right ? RENDER_WIDTH - pip.width - coords.right : RENDER_WIDTH / 2 - pip.width / 2),
  };
}

// Caption position calculation (1-9 scale to pixels)
function getCaptionTop(position: number): number {
  // Position 1 = 5%, Position 9 = 85%
  const percent = ((position - 1) / 8) * 0.8 + 0.05;
  return RENDER_HEIGHT * percent;
}

interface ScenePreviewProps {
  scene: Scene;
  avatarUrl?: string;
  previewingUrl?: string | null;
  captionSettings?: CaptionSettings | null;
  showCaption?: boolean;
  className?: string;
  onReplay?: () => void;
}

export function ScenePreview({
  scene,
  avatarUrl,
  previewingUrl,
  captionSettings,
  showCaption = true,
  className = '',
  onReplay,
}: ScenePreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  
  // Calculate scale based on container width
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setScale(containerWidth / RENDER_WIDTH);
      }
    };
    
    updateScale();
    const observer = new ResizeObserver(updateScale);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);
  
  const brollUrl = previewingUrl || scene.broll?.url;
  const isComponent = scene.broll?.source === 'component' && scene.broll?.componentType;
  const isVideoBroll = scene.broll?.mediaType === 'video' || (brollUrl && /\.(mp4|webm|mov)(\?|$)/i.test(brollUrl));
  const captionPosition = scene.captionPositionOverride || captionSettings?.position || 7;

  // Render b-roll area content (image, video, or live component)
  const isLottie = scene.broll?.source === 'lottie' && scene.broll?.url;

  const renderBrollContent = (areaWidth: number, areaHeight: number) => {
    if (isLottie) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0A1629', color: '#34A49F', gap: 4 }}>
          <span style={{ fontSize: 28 * scale }}>🎬</span>
          <span style={{ fontSize: 10 * scale, opacity: 0.7 }}>Lottie Animation</span>
        </div>
      );
    }
    if (isComponent && scene.broll?.componentType) {
      return (
        <ComponentPreview
          componentType={scene.broll.componentType}
          width={areaWidth}
          height={areaHeight}
          notes={scene.broll.componentNotes}
          componentProps={scene.broll.componentProps as Record<string, unknown>}
        />
      );
    }
    if (brollUrl && isVideoBroll) {
      return <video src={brollUrl} muted autoPlay loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
    }
    if (brollUrl) {
      return <img src={brollUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
    }
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 * scale, color: '#64748b' }}>
        🖼️
      </div>
    );
  };

  // Render caption
  const renderCaption = () => {
    if (!showCaption || !captionSettings) return null;
    
    const words = scene.transcript.split(' ').slice(0, captionSettings.wordsShown || 2).join(' ');
    const top = getCaptionTop(captionPosition);
    const edgeRadius = EDGE_STYLES.find(e => e.id === captionSettings.edgeStyle)?.radius || 9999;
    
    // Apply text case
    let displayText = words;
    switch (captionSettings.textCase) {
      case 'uppercase': displayText = words.toUpperCase(); break;
      case 'lowercase': displayText = words.toLowerCase(); break;
      case 'capitalize': displayText = words.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '); break;
    }
    
    return (
      <div
        style={{
          position: 'absolute',
          top: top * scale,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: captionSettings.backgroundColor || '#34A49F',
          color: captionSettings.textColor || '#FFFFFF',
          fontFamily: `'${captionSettings.fontFamily || 'Poppins'}', sans-serif`,
          fontWeight: captionSettings.fontWeight || 600,
          fontSize: (captionSettings.fontSize || 32) * scale,
          padding: `${(captionSettings.padding || 16) * scale * 0.5}px ${(captionSettings.padding || 16) * scale}px`,
          borderRadius: edgeRadius === 9999 ? 9999 : edgeRadius * scale,
          whiteSpace: 'nowrap',
          zIndex: 20,
        }}
      >
        {displayText}
      </div>
    );
  };
  
  // Render based on scene type
  const renderContent = () => {
    const layout = LAYOUTS[scene.type as keyof typeof LAYOUTS];
    
    switch (scene.type) {
      case 'SPEAKER_ONLY':
        return (
          <>
            <div style={{ position: 'absolute', inset: 0, background: '#000' }} />
            {avatarUrl ? (
              <video
                src={avatarUrl}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: LAYOUTS.SPEAKER_ONLY.objectPosition,
                }}
                muted
                playsInline
              />
            ) : (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 * scale }}>🎤</div>
            )}
          </>
        );
        
      case 'SPLIT_SCREEN':
        const split = LAYOUTS.SPLIT_SCREEN;
        return (
          <>
            {/* B-roll top */}
            <div
              style={{
                position: 'absolute',
                top: split.broll.top * scale,
                left: 0,
                width: '100%',
                height: split.broll.height * scale,
                background: '#0A1629',
                overflow: 'hidden',
              }}
            >
              {renderBrollContent(split.broll.width, split.broll.height)}
            </div>
            {/* Avatar bottom */}
            <div
              style={{
                position: 'absolute',
                top: split.avatar.top * scale,
                left: 0,
                width: '100%',
                height: split.avatar.height * scale,
                background: '#0a0a0a',
                overflow: 'hidden',
              }}
            >
              {avatarUrl ? (
                <video
                  src={avatarUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: split.avatarObjectPosition,
                  }}
                  muted
                  playsInline
                />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 * scale }}>👤</div>
              )}
            </div>
          </>
        );
        
      case 'CIRCLE_PIP':
        const pip = LAYOUTS.CIRCLE_PIP.pip;
        const pipCoords = getPipCoords(scene.pipPosition);
        return (
          <>
            {/* Full b-roll */}
            <div style={{ position: 'absolute', inset: 0, background: '#0A1629', overflow: 'hidden' }}>
              {renderBrollContent(RENDER_WIDTH, RENDER_HEIGHT)}
            </div>
            {/* Circle PIP */}
            <div
              style={{
                position: 'absolute',
                top: pipCoords.top * scale,
                left: pipCoords.left * scale,
                width: pip.width * scale,
                height: pip.height * scale,
                borderRadius: '50%',
                overflow: 'hidden',
                border: `${Math.max(2, 3 * scale)}px solid white`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                zIndex: 10,
              }}
            >
              {avatarUrl ? (
                <video
                  src={avatarUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: LAYOUTS.CIRCLE_PIP.avatarObjectPosition,
                  }}
                  muted
                  playsInline
                />
              ) : (
                <div style={{ width: '100%', height: '100%', background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 * scale }}>👤</div>
              )}
            </div>
          </>
        );
        
      case 'FULL_BROLL':
      case 'FULL_BLEED_BROLL':
        return (
          <div style={{ position: 'absolute', inset: 0, background: '#0A1629', overflow: 'hidden' }}>
            {renderBrollContent(RENDER_WIDTH, RENDER_HEIGHT)}
          </div>
        );
        
      case 'OVERLAY':
        const overlayUrl = scene.overlay?.pngUrl || (scene.overlayTemplate ? OVERLAY_TEMPLATES[scene.overlayTemplate]?.overlay : null);
        return (
          <div style={{ position: 'absolute', inset: 0, background: '#0a1629' }}>
            {overlayUrl && (
              <img src={overlayUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            )}
          </div>
        );
        
      default:
        return <div style={{ position: 'absolute', inset: 0, background: '#333' }} />;
    }
  };
  
  return (
    <div
      ref={containerRef}
      className={`relative bg-gray-900 overflow-hidden ${className}`}
      style={{ aspectRatio: '9/16' }}
    >
      {renderContent()}
      {renderCaption()}
      
      {/* Replay button */}
      {onReplay && scene.effect && scene.effect !== 'none' && (
        <button
          onClick={onReplay}
          style={{
            position: 'absolute',
            bottom: 8 * scale,
            right: 8 * scale,
            padding: `${4 * scale}px ${8 * scale}px`,
            background: 'rgba(0,0,0,0.6)',
            color: 'white',
            fontSize: 10 * scale,
            borderRadius: 9999,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          ▶️ Replay
        </button>
      )}
    </div>
  );
}
